import { useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import CountryMultiSelect from "@/components/CountryMultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIME_OPTIONS = [
  { value: "any", label: "Qualsiasi" },
  { value: "1",   label: "Entro 1 ora" },
  { value: "2",   label: "Entro 2 ore" },
  { value: "6",   label: "Entro 6 ore" },
];

const PICKUP_SOON_TIME_OPTIONS = [
  { value: "1",  label: "Entro 1 ora" },
  { value: "2",  label: "Entro 2 ore" },
  { value: "6",  label: "Entro 6 ore" },
];

const LOCAL_TIME_OPTIONS = [
  { value: "any", label: "Qualsiasi" },
  { value: "24",  label: "Entro 1 giorno" },
  { value: "48",  label: "Entro 2 giorni" },
  { value: "72",  label: "Entro 3 giorni" },
  { value: "96",  label: "Entro 4 giorni" },
  { value: "120", label: "Entro 5 giorni" },
  { value: "144", label: "Entro 6 giorni" },
  { value: "168", label: "Entro 7 giorni" },
  { value: "192", label: "Entro 8 giorni" },
  { value: "216", label: "Entro 9 giorni" },
  { value: "240", label: "Entro 10 giorni" },
];

const MULTIDAY_TIME_OPTIONS = [
  { value: "24",  label: "1 giorno (24h)" },
  { value: "48",  label: "2 giorni (48h)" },
  { value: "72",  label: "3 giorni (72h)" },
  { value: "96",  label: "4 giorni (96h)" },
  { value: "120", label: "5 giorni (120h)" },
  { value: "144", label: "6 giorni (144h)" },
  { value: "168", label: "7 giorni (168h)" },
  { value: "192", label: "8 giorni (192h)" },
  { value: "216", label: "9 giorni (216h)" },
  { value: "240", label: "10 giorni (240h)" },
];

export default function SearchForm({ categories, loadingCategories, onSubmit, submitting, mode = "standard", user, onSave }) {
  const isMultiday = mode === "multiday";
  const isPier = mode === "pier";
  const isLocal = mode === "local";
  const isPickup = mode === "pickup" || mode === "pickup_or_ship";
  const isPickupSoon = mode === "pickup_soon";
  const activeTimeOptions = isPickupSoon ? PICKUP_SOON_TIME_OPTIONS
    : (isMultiday || isPier || isPickup) ? MULTIDAY_TIME_OPTIONS
    : isLocal ? LOCAL_TIME_OPTIONS
    : TIME_OPTIONS;

  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("any");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [maxHours, setMaxHours] = useState(isPickupSoon ? "1" : (isMultiday || isPier || isPickup) ? "24" : "any");
  const [countries, setCountries] = useState(isLocal ? ["IT"] : []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isMultiday && !q.trim()) {
      toast.error("Le parole chiave sono obbligatorie per la ricerca per giorni.");
      return;
    }
    onSubmit({
      q: q.trim(),
      categoryId: categoryId !== "any" ? categoryId : undefined,
      minPrice: minPrice ? Number(minPrice) : undefined,
      maxPrice: maxPrice ? Number(maxPrice) : undefined,
      maxHoursRemaining: maxHours !== "any" ? Number(maxHours) : undefined,
      sellerCountries: countries.length ? countries : undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 px-6 py-8 lg:px-10 lg:py-10"
      data-testid="search-form"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-8">
        {/* Keywords */}
        <div className="lg:col-span-5">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            Parole chiave{isMultiday && <span className="text-gold ml-1">*</span>}
            {(isPickup || isPickupSoon) && <span className="text-white/30 ml-1 normal-case tracking-normal">(opzionale)</span>}
          </label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="es. Rolex, Porsche, Picasso…"
            className="input-luxury w-full text-2xl font-display py-2"
            data-testid="search-input"
          />
        </div>

        {/* Category */}
        <div className="lg:col-span-3">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            Categoria
          </label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-2 h-auto text-base font-body focus:ring-0 focus:border-gold data-[placeholder]:text-white/35"
              data-testid="category-select"
            >
              <SelectValue placeholder={loadingCategories ? "Caricamento…" : "Tutte le categorie"} />
            </SelectTrigger>
            <SelectContent
              className="bg-surface border-white/10 max-h-80"
              data-testid="category-select-content"
            >
              <SelectItem value="any" data-testid="category-option-any">Tutte le categorie</SelectItem>
              {categories.map((c) => (
                <SelectItem
                  key={c.id}
                  value={String(c.id)}
                  data-testid={`category-option-${c.id}`}
                >
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Min price */}
        <div className="lg:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            Prezzo min (€)
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            placeholder="0"
            className="input-luxury w-full text-base py-2"
            data-testid="min-price-input"
          />
        </div>

        {/* Max price */}
        <div className="lg:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            Prezzo max (€)
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="∞"
            className="input-luxury w-full text-base py-2"
            data-testid="price-input"
          />
        </div>

        {/* Time remaining */}
        <div className="lg:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            {(isMultiday || isPier || isPickup) ? "Giorni rimanenti" : isPickupSoon ? "Tempo rimanente (max 6h)" : "Tempo rimanente"}
          </label>
          <Select value={maxHours} onValueChange={setMaxHours}>
            <SelectTrigger
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-2 h-auto text-base font-body focus:ring-0 focus:border-gold"
              data-testid="time-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10">
              {activeTimeOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`time-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Seller countries */}
        <div className="lg:col-span-12">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
            Paese venditore (multi-selezione)
          </label>
          <CountryMultiSelect value={countries} onChange={setCountries} />
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        {user && onSave ? (
          <Button
            type="button"
            onClick={() => onSave({ q: q.trim(), categoryId: categoryId !== "any" ? categoryId : undefined, minPrice: minPrice ? Number(minPrice) : undefined, maxPrice: maxPrice ? Number(maxPrice) : undefined, maxHoursRemaining: maxHours !== "any" ? Number(maxHours) : undefined, sellerCountries: countries.length ? countries : undefined })}
            className="rounded-none border border-white/20 text-white/60 hover:border-gold hover:text-gold transition-all duration-300 px-6 py-6 text-xs uppercase tracking-[0.22em] font-medium"
            data-testid="save-search-button"
          >
            Salva ricerca
          </Button>
        ) : (
          <div />
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-none bg-white text-black hover:bg-gold hover:text-black transition-all duration-300 px-10 py-6 text-sm uppercase tracking-[0.25em] font-medium disabled:opacity-50"
          data-testid="search-submit-button"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Ricerca…
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Cerca
            </>
          )}
        </Button>
      </div>
    </form>
  );
}